import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import api from '../../services/api';

const emptySlider = {
  id: null,
  titulo: '',
  subtitulo: '',
  imagen_url: '',
  boton_texto: '',
  boton_link: '',
  orden: 0,
  duracion_segundos: 5,
  activo: 1
};

const emptyCategoria = {
  id: null,
  nombre: '',
  descripcion: '',
  imagen_url: '',
  formato_imagen: 'vertical',
  orden: 0,
  activo: 1
};

const emptyProducto = {
  id: null,
  idcategoria: '',
  nombre: '',
  descripcion: '',
  precio: '',
  imagen_principal: '',
  destacado: 0,
  orden: 0,
  activo: 1
};

const defaultConfig = {
  site_title: '',
  site_subtitle: '',

  hero_titulo: '',
  hero_subtitulo: '',
  hero_boton_texto: '',
  hero_boton_link: '',

  quienes_somos_titulo: '',
  quienes_somos_texto: '',
  quienes_somos_imagen: '',

  contact_badge: '',
  contact_titulo: '',
  contact_texto: '',
  contact_card_subtitulo: '',
  contact_whatsapp_boton: '',
  contact_catalogo_boton: '',

  telefono_publico: '',
  whatsapp_publico: '',
  correo_publico: '',
  direccion_publica: '',
  google_maps_url: '',

  instagram_url: '',
  facebook_url: '',
  tiktok_url: '',

  footer_descripcion: '',
  footer_columna_titulo: '',
  footer_columna_texto: '',
  footer_boton_texto: '',
  footer_boton_link: '',
  footer_frase_final: '',
  whatsapp_boton_texto: '',

  catalogo_titulo: '',
  catalogo_subtitulo: ''
};

const configLabels = {
  site_title: 'Título del sitio',
  site_subtitle: 'Subtítulo del sitio',

  hero_titulo: 'Hero - título principal',
  hero_subtitulo: 'Hero - subtítulo principal',
  hero_boton_texto: 'Hero - texto botón principal',
  hero_boton_link: 'Hero - link botón principal',

  quienes_somos_titulo: 'Quiénes somos - título',
  quienes_somos_texto: 'Quiénes somos - texto',
  quienes_somos_imagen: 'Quiénes somos - imagen',

  contact_badge: 'Contacto - etiqueta superior',
  contact_titulo: 'Contacto - título',
  contact_texto: 'Contacto - texto',
  contact_card_subtitulo: 'Contacto - subtítulo tarjeta',
  contact_whatsapp_boton: 'Contacto - texto botón WhatsApp',
  contact_catalogo_boton: 'Contacto - texto botón catálogo',

  telefono_publico: 'Teléfono público',
  whatsapp_publico: 'WhatsApp público',
  correo_publico: 'Correo público',
  direccion_publica: 'Dirección pública',
  google_maps_url: 'Google Maps URL',

  instagram_url: 'Instagram URL',
  facebook_url: 'Facebook URL',
  tiktok_url: 'TikTok URL',

  footer_descripcion: 'Footer - descripción',
  footer_columna_titulo: 'Footer - título columna derecha',
  footer_columna_texto: 'Footer - texto columna derecha',
  footer_boton_texto: 'Footer - texto botón',
  footer_boton_link: 'Footer - link botón',
  footer_frase_final: 'Footer - frase inferior',
  whatsapp_boton_texto: 'Botón flotante WhatsApp',

  catalogo_titulo: 'Catálogo - título',
  catalogo_subtitulo: 'Catálogo - subtítulo'
};

const configSections = [
  {
    title: 'Identidad del sitio',
    description: 'Nombre principal y descripción corta de Fresh Exports.',
    keys: ['site_title', 'site_subtitle']
  },
  {
    title: 'Hero principal',
    description: 'Textos de respaldo del banner principal cuando el slider no tenga texto.',
    keys: ['hero_titulo', 'hero_subtitulo', 'hero_boton_texto', 'hero_boton_link']
  },
  {
    title: 'Quiénes somos',
    description: 'Contenido institucional de la sección quiénes somos.',
    keys: ['quienes_somos_titulo', 'quienes_somos_texto', 'quienes_somos_imagen']
  },
  {
    title: 'Contáctanos',
    description: 'Textos que se muestran en la sección de contacto de la página principal.',
    keys: [
      'contact_badge',
      'contact_titulo',
      'contact_texto',
      'contact_card_subtitulo',
      'contact_whatsapp_boton',
      'contact_catalogo_boton'
    ]
  },
  {
    title: 'Datos de contacto',
    description: 'Información pública que verá el cliente en contacto y pie de página.',
    keys: [
      'telefono_publico',
      'whatsapp_publico',
      'correo_publico',
      'direccion_publica',
      'google_maps_url'
    ]
  },
  {
    title: 'Redes sociales',
    description: 'Links públicos a redes sociales.',
    keys: ['instagram_url', 'facebook_url', 'tiktok_url']
  },
  {
    title: 'Pie de página',
    description: 'Textos configurables para el footer global de la web.',
    keys: [
      'footer_descripcion',
      'footer_columna_titulo',
      'footer_columna_texto',
      'footer_boton_texto',
      'footer_boton_link',
      'footer_frase_final',
      'whatsapp_boton_texto'
    ]
  },
  {
    title: 'Catálogo público',
    description: 'Textos principales de la página de catálogo.',
    keys: ['catalogo_titulo', 'catalogo_subtitulo']
  }
];

function isLongConfigField(key) {
  return (
    key.includes('texto') ||
    key.includes('descripcion') ||
    key.includes('subtitulo') ||
    key.includes('frase')
  );
}

function isUrlField(key) {
  return key.includes('url') || key.includes('link');
}

function getInputType(key) {
  if (key === 'correo_publico') return 'email';
  if (isUrlField(key)) return 'url';
  return 'text';
}

function normalizeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export default function WebManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState('');

  const [config, setConfig] = useState({ ...defaultConfig });

  const [slider, setSlider] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [productos, setProductos] = useState([]);

  const [sliderOpen, setSliderOpen] = useState(false);
  const [categoriaOpen, setCategoriaOpen] = useState(false);
  const [productoOpen, setProductoOpen] = useState(false);

  const [sliderSaving, setSliderSaving] = useState(false);
  const [categoriaSaving, setCategoriaSaving] = useState(false);
  const [productoSaving, setProductoSaving] = useState(false);

  const [sliderForm, setSliderForm] = useState({ ...emptySlider });
  const [categoriaForm, setCategoriaForm] = useState({ ...emptyCategoria });
  const [productoForm, setProductoForm] = useState({ ...emptyProducto });

  const categoriasMap = useMemo(() => {
    const map = new Map();
    categorias.forEach((c) => map.set(Number(c.id), c.nombre));
    return map;
  }, [categorias]);

  const loadAll = async () => {
    setLoading(true);

    try {
      const [
        { data: cfgData },
        { data: sliderData },
        { data: categoriasData },
        { data: productosData }
      ] = await Promise.all([
        api.get('/api/web/config'),
        api.get('/api/web-admin/slider'),
        api.get('/api/web-admin/categorias'),
        api.get('/api/web-admin/productos')
      ]);

      setConfig({
        ...defaultConfig,
        ...(cfgData || {})
      });

      setSlider(Array.isArray(sliderData) ? sliderData : []);
      setCategorias(Array.isArray(categoriasData) ? categoriasData : []);
      setProductos(Array.isArray(productosData) ? productosData : []);
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || 'Error cargando módulo web');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveConfig = async () => {
    setSaving(true);

    try {
      await api.put('/api/web-admin/config', config);
      alert('Configuración guardada correctamente');
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || 'Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('imagen', file);

    const { data } = await api.post('/api/web-admin/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    return data?.path || '';
  };

  const handleUploadToForm = async (file, target, field) => {
    if (!file) return;

    setUploadingField(`${target}.${field}`);

    try {
      const path = await uploadFile(file);
      if (!path) return;

      if (target === 'slider') {
        setSliderForm((prev) => ({ ...prev, [field]: path }));
      }

      if (target === 'categoria') {
        setCategoriaForm((prev) => ({ ...prev, [field]: path }));
      }

      if (target === 'producto') {
        setProductoForm((prev) => ({ ...prev, [field]: path }));
      }
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || 'Error subiendo imagen');
    } finally {
      setUploadingField('');
    }
  };

  const handleConfigImageUpload = async (file, field) => {
    if (!file) return;

    setUploadingField(`config.${field}`);

    try {
      const path = await uploadFile(file);
      if (!path) return;

      setConfig((prev) => ({
        ...prev,
        [field]: path
      }));
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || 'Error subiendo imagen');
    } finally {
      setUploadingField('');
    }
  };

  const saveSlider = async () => {
    setSliderSaving(true);

    try {
      const payload = {
        ...sliderForm,
        orden: normalizeNumber(sliderForm.orden, 0),
        duracion_segundos: Math.max(1, normalizeNumber(sliderForm.duracion_segundos, 5)),
        activo: Number(sliderForm.activo) ? 1 : 0
      };

      if (sliderForm.id) {
        await api.put(`/api/web-admin/slider/${sliderForm.id}`, payload);
      } else {
        await api.post('/api/web-admin/slider', payload);
      }

      setSliderOpen(false);
      setSliderForm({ ...emptySlider });
      await loadAll();
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || 'Error guardando slider');
    } finally {
      setSliderSaving(false);
    }
  };

  const saveCategoria = async () => {
    setCategoriaSaving(true);

    try {
      const payload = {
        ...categoriaForm,
        orden: normalizeNumber(categoriaForm.orden, 0),
        activo: Number(categoriaForm.activo) ? 1 : 0
      };

      if (categoriaForm.id) {
        await api.put(`/api/web-admin/categorias/${categoriaForm.id}`, payload);
      } else {
        await api.post('/api/web-admin/categorias', payload);
      }

      setCategoriaOpen(false);
      setCategoriaForm({ ...emptyCategoria });
      await loadAll();
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || 'Error guardando categoría');
    } finally {
      setCategoriaSaving(false);
    }
  };

  const saveProducto = async () => {
    setProductoSaving(true);

    try {
      const payload = {
        ...productoForm,
        idcategoria: productoForm.idcategoria === '' ? '' : Number(productoForm.idcategoria),
        precio: productoForm.precio === '' ? '' : Number(productoForm.precio),
        orden: normalizeNumber(productoForm.orden, 0),
        destacado: Number(productoForm.destacado) ? 1 : 0,
        activo: Number(productoForm.activo) ? 1 : 0
      };

      if (productoForm.id) {
        await api.put(`/api/web-admin/productos/${productoForm.id}`, payload);
      } else {
        await api.post('/api/web-admin/productos', payload);
      }

      setProductoOpen(false);
      setProductoForm({ ...emptyProducto });
      await loadAll();
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || 'Error guardando producto');
    } finally {
      setProductoSaving(false);
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este registro?')) return;

    try {
      await api.delete(`/api/web-admin/${type}/${id}`);
      await loadAll();
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || 'Error eliminando');
    }
  };

  const renderImagePreview = (src, alt) => {
    if (!src) return null;

    return (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          maxWidth: 440,
          maxHeight: 240,
          objectFit: 'cover',
          borderRadius: 2,
          border: '1px solid #ddd',
          bgcolor: '#f7f7f7'
        }}
      />
    );
  };

  const renderConfigField = (key) => {
    const isLongText = isLongConfigField(key);
    const isImage = key === 'quienes_somos_imagen';
    const isUploading = uploadingField === `config.${key}`;

    return (
      <Grid item xs={12} md={isLongText || isImage ? 12 : 6} key={key}>
        <Stack spacing={1.2}>
          <TextField
            fullWidth
            multiline={isLongText}
            minRows={isLongText ? 4 : 1}
            type={getInputType(key)}
            label={configLabels[key] || key}
            value={config[key] || ''}
            onChange={(e) =>
              setConfig((prev) => ({
                ...prev,
                [key]: e.target.value
              }))
            }
            helperText={
              isImage
                ? 'Puedes escribir una URL externa o subir una imagen desde tu equipo.'
                : isUrlField(key)
                  ? 'Puedes usar una ruta interna como /catalogo o un link completo https://...'
                  : ' '
            }
          />

          {isImage && (
            <>
              <Button
                component="label"
                variant="outlined"
                size="small"
                disabled={isUploading}
                sx={{ width: 'fit-content' }}
              >
                {isUploading ? 'Subiendo...' : 'Subir imagen'}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    handleConfigImageUpload(file, key);
                    e.target.value = '';
                  }}
                />
              </Button>

              {renderImagePreview(config[key], configLabels[key] || key)}
            </>
          )}
        </Stack>
      </Grid>
    );
  };

  const renderImageUploadButton = ({ label = 'Subir imagen', target, field, disabled }) => (
    <Button component="label" variant="outlined" disabled={disabled}>
      {disabled ? 'Subiendo...' : label}
      <input
        hidden
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          handleUploadToForm(file, target, field);
          e.target.value = '';
        }}
      />
    </Button>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography>Cargando módulo web...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Página Web / Catálogo
      </Typography>

      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Administra textos públicos, contacto, footer, slider, categorías y productos.
      </Typography>

      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Configuración general
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Estos textos alimentan la web pública y el pie de página.
              </Typography>
            </Box>

            <Button variant="contained" onClick={saveConfig} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar configuración'}
            </Button>
          </Stack>

          <Stack spacing={4}>
            {configSections.map((section) => (
              <Box key={section.title}>
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 800 }}>{section.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {section.description}
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  {section.keys.map(renderConfigField)}
                </Grid>

                <Divider sx={{ mt: 3 }} />
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Slider principal
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Imágenes y mensajes principales del hero.
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => {
                setSliderForm({
                  ...emptySlider,
                  duracion_segundos: 5
                });
                setSliderOpen(true);
              }}
            >
              Nuevo slider
            </Button>
          </Stack>

          <Stack spacing={2}>
            {slider.length === 0 && (
              <Alert severity="info">Todavía no tienes sliders registrados.</Alert>
            )}

            {slider.map((item) => (
              <Alert
                key={item.id}
                severity={Number(item.activo) ? 'success' : 'warning'}
                action={
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      onClick={() => {
                        setSliderForm({
                          ...emptySlider,
                          ...item,
                          orden: normalizeNumber(item.orden, 0),
                          duracion_segundos: normalizeNumber(item.duracion_segundos, 5),
                          activo: Number(item.activo) ? 1 : 0
                        });
                        setSliderOpen(true);
                      }}
                    >
                      Editar
                    </Button>

                    <Button
                      size="small"
                      color="error"
                      onClick={() => deleteItem('slider', item.id)}
                    >
                      Eliminar
                    </Button>
                  </Stack>
                }
              >
                #{item.orden} - {item.titulo || 'Sin título'} ·{' '}
                {normalizeNumber(item.duracion_segundos, 5)}s
              </Alert>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Categorías
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Familias de productos para el catálogo.
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => {
                setCategoriaForm({ ...emptyCategoria });
                setCategoriaOpen(true);
              }}
            >
              Nueva categoría
            </Button>
          </Stack>

          <Stack spacing={2}>
            {categorias.length === 0 && (
              <Alert severity="info">Todavía no tienes categorías registradas.</Alert>
            )}

            {categorias.map((item) => (
              <Alert
                key={item.id}
                severity={Number(item.activo) ? 'success' : 'warning'}
                action={
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      onClick={() => {
                        setCategoriaForm({
                          ...emptyCategoria,
                          ...item,
                          orden: normalizeNumber(item.orden, 0),
                          activo: Number(item.activo) ? 1 : 0
                        });
                        setCategoriaOpen(true);
                      }}
                    >
                      Editar
                    </Button>

                    <Button
                      size="small"
                      color="error"
                      onClick={() => deleteItem('categorias', item.id)}
                    >
                      Eliminar
                    </Button>
                  </Stack>
                }
              >
                #{item.orden} - {item.nombre} · {item.formato_imagen === 'cuadrada' ? 'Cuadrada' : 'Vertical'}
              </Alert>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Productos
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Productos visibles en el catálogo público.
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => {
                setProductoForm({ ...emptyProducto });
                setProductoOpen(true);
              }}
            >
              Nuevo producto
            </Button>
          </Stack>

          <Stack spacing={2}>
            {productos.length === 0 && (
              <Alert severity="info">Todavía no tienes productos registrados.</Alert>
            )}

            {productos.map((item) => (
              <Alert
                key={item.id}
                severity={Number(item.activo) ? 'success' : 'warning'}
                action={
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      onClick={() => {
                        setProductoForm({
                          ...emptyProducto,
                          ...item,
                          idcategoria:
                            item.idcategoria === null || item.idcategoria === undefined
                              ? ''
                              : String(item.idcategoria),
                          precio: item.precio ?? '',
                          orden: normalizeNumber(item.orden, 0),
                          destacado: Number(item.destacado) ? 1 : 0,
                          activo: Number(item.activo) ? 1 : 0
                        });
                        setProductoOpen(true);
                      }}
                    >
                      Editar
                    </Button>

                    <Button
                      size="small"
                      color="error"
                      onClick={() => deleteItem('productos', item.id)}
                    >
                      Eliminar
                    </Button>
                  </Stack>
                }
              >
                #{item.orden} - {item.nombre} ·{' '}
                {categoriasMap.get(Number(item.idcategoria)) ||
                  item.categoria_nombre ||
                  'Sin categoría'}
              </Alert>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={sliderOpen} onClose={() => setSliderOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{sliderForm.id ? 'Editar slider' : 'Nuevo slider'}</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Título"
              value={sliderForm.titulo}
              onChange={(e) => setSliderForm((prev) => ({ ...prev, titulo: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Subtítulo"
              value={sliderForm.subtitulo}
              onChange={(e) => setSliderForm((prev) => ({ ...prev, subtitulo: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />

            <TextField
              label="Botón texto"
              value={sliderForm.boton_texto}
              onChange={(e) => setSliderForm((prev) => ({ ...prev, boton_texto: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Botón link"
              value={sliderForm.boton_link}
              onChange={(e) => setSliderForm((prev) => ({ ...prev, boton_link: e.target.value }))}
              helperText="Ejemplo: /catalogo o https://..."
              fullWidth
            />

            <TextField
              label="Imagen URL / Path"
              value={sliderForm.imagen_url}
              onChange={(e) => setSliderForm((prev) => ({ ...prev, imagen_url: e.target.value }))}
              fullWidth
            />

            {renderImageUploadButton({
              target: 'slider',
              field: 'imagen_url',
              disabled: uploadingField === 'slider.imagen_url'
            })}

            {renderImagePreview(sliderForm.imagen_url, 'Slider')}

            <TextField
              label="Orden"
              type="number"
              value={sliderForm.orden}
              onChange={(e) => setSliderForm((prev) => ({ ...prev, orden: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Duración (segundos)"
              type="number"
              value={sliderForm.duracion_segundos}
              onChange={(e) =>
                setSliderForm((prev) => ({
                  ...prev,
                  duracion_segundos: e.target.value
                }))
              }
              inputProps={{ min: 1, max: 60, step: 1 }}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(Number(sliderForm.activo))}
                  onChange={(e) =>
                    setSliderForm((prev) => ({
                      ...prev,
                      activo: e.target.checked ? 1 : 0
                    }))
                  }
                />
              }
              label="Activo"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSliderOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveSlider} disabled={sliderSaving}>
            {sliderSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={categoriaOpen} onClose={() => setCategoriaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{categoriaForm.id ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              value={categoriaForm.nombre}
              onChange={(e) => setCategoriaForm((prev) => ({ ...prev, nombre: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Descripción"
              value={categoriaForm.descripcion}
              onChange={(e) =>
                setCategoriaForm((prev) => ({ ...prev, descripcion: e.target.value }))
              }
              fullWidth
              multiline
              minRows={3}
            />

            <TextField
              label="Imagen URL / Path"
              value={categoriaForm.imagen_url}
              onChange={(e) =>
                setCategoriaForm((prev) => ({ ...prev, imagen_url: e.target.value }))
              }
              fullWidth
            />

            {renderImageUploadButton({
              target: 'categoria',
              field: 'imagen_url',
              disabled: uploadingField === 'categoria.imagen_url'
            })}

            {renderImagePreview(categoriaForm.imagen_url, 'Categoría')}

            <TextField select fullWidth label="Formato de las imágenes en el catálogo"
              value={categoriaForm.formato_imagen || 'vertical'}
              onChange={(e) => setCategoriaForm((prev) => ({ ...prev, formato_imagen: e.target.value }))}
              helperText="Se aplica a todos los productos de esta categoría. Puedes cambiarlo cuando quieras.">
              <MenuItem value="cuadrada">Cuadrada · rosas y claveles</MenuItem>
              <MenuItem value="vertical">Vertical · alhelí y flores de tallo largo</MenuItem>
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8f6f4', borderRadius: 2 }}>
              <Box sx={{ width: 96, aspectRatio: categoriaForm.formato_imagen === 'cuadrada' ? '1 / 1' : '2 / 3',
                border: '2px solid #94703b', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', flexShrink: 0 }}>
                {categoriaForm.imagen_url && <Box component="img" src={categoriaForm.imagen_url} alt=""
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
              </Box>
              <Typography variant="body2" color="text.secondary">
                Vista previa del formato {categoriaForm.formato_imagen === 'cuadrada' ? 'cuadrado' : 'vertical'}.
                Las imágenes originales se conservan.
              </Typography>
            </Box>

            <TextField
              label="Orden"
              type="number"
              value={categoriaForm.orden}
              onChange={(e) => setCategoriaForm((prev) => ({ ...prev, orden: e.target.value }))}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(Number(categoriaForm.activo))}
                  onChange={(e) =>
                    setCategoriaForm((prev) => ({
                      ...prev,
                      activo: e.target.checked ? 1 : 0
                    }))
                  }
                />
              }
              label="Activo"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setCategoriaOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveCategoria} disabled={categoriaSaving}>
            {categoriaSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={productoOpen} onClose={() => setProductoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{productoForm.id ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Categoría"
              value={productoForm.idcategoria}
              onChange={(e) =>
                setProductoForm((prev) => ({ ...prev, idcategoria: e.target.value }))
              }
              fullWidth
            >
              {categorias.map((cat) => (
                <MenuItem key={cat.id} value={String(cat.id)}>
                  {cat.nombre}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Nombre"
              value={productoForm.nombre}
              onChange={(e) => setProductoForm((prev) => ({ ...prev, nombre: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Descripción"
              value={productoForm.descripcion}
              onChange={(e) =>
                setProductoForm((prev) => ({ ...prev, descripcion: e.target.value }))
              }
              fullWidth
              multiline
              minRows={4}
            />

            <TextField
              label="Precio"
              type="number"
              value={productoForm.precio}
              onChange={(e) => setProductoForm((prev) => ({ ...prev, precio: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Imagen principal URL / Path"
              value={productoForm.imagen_principal}
              onChange={(e) =>
                setProductoForm((prev) => ({
                  ...prev,
                  imagen_principal: e.target.value
                }))
              }
              fullWidth
            />

            {renderImageUploadButton({
              target: 'producto',
              field: 'imagen_principal',
              disabled: uploadingField === 'producto.imagen_principal'
            })}

            {renderImagePreview(productoForm.imagen_principal, 'Producto')}

            <TextField
              label="Orden"
              type="number"
              value={productoForm.orden}
              onChange={(e) => setProductoForm((prev) => ({ ...prev, orden: e.target.value }))}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(Number(productoForm.destacado))}
                  onChange={(e) =>
                    setProductoForm((prev) => ({
                      ...prev,
                      destacado: e.target.checked ? 1 : 0
                    }))
                  }
                />
              }
              label="Destacado"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(Number(productoForm.activo))}
                  onChange={(e) =>
                    setProductoForm((prev) => ({
                      ...prev,
                      activo: e.target.checked ? 1 : 0
                    }))
                  }
                />
              }
              label="Activo"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setProductoOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveProducto} disabled={productoSaving}>
            {productoSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
